import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Markdown } from "../components/agent/Markdown";
import { api } from "../lib/api";

type RevisionRead = {
  head: { output_id: string; brand_id: string; campaign_id: string; family: string; kind: string | null };
  revision: { revision_id: string; created_at: string; is_current: boolean } | null;
  state: "available" | "revision_missing" | "head_revision_missing" | "body_unavailable" | "head_not_ready";
  content: { format: "markdown" | "text"; body: string } | { format: "structured"; title?: string | null; fields: Array<{ key: string; label: string; value: string | number | boolean | null; render?: string }> } | null;
};

function readExactRevision(value: RevisionRead, ids: { brandId: string; campaignId: string; outputId: string; revisionId: string }) {
  if (value.head.brand_id.toLowerCase() !== ids.brandId.toLowerCase()
    || value.head.campaign_id.toLowerCase() !== ids.campaignId.toLowerCase()
    || value.head.output_id !== ids.outputId
    || value.revision?.revision_id !== ids.revisionId) {
    throw new Error("The returned output revision did not match the requested brand, campaign, output, and revision.");
  }
  return value;
}

export function CampaignDailyOutputRevision() {
  const { brandId = "", campaignId = "", outputId = "", revisionId = "" } = useParams();
  const query = useQuery({
    queryKey: ["campaign-daily-output-revision", brandId, campaignId, outputId, revisionId],
    enabled: !!brandId && !!campaignId && !!outputId && !!revisionId,
    retry: false,
    queryFn: async ({ signal }) => {
      const response = await api.get<{ data: RevisionRead }>(
        `/api/v1/brands/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}/outputs/${encodeURIComponent(outputId)}/revisions/${encodeURIComponent(revisionId)}`,
        signal,
      );
      return readExactRevision(response.data, { brandId, campaignId, outputId, revisionId });
    },
  });

  return <main className="mx-auto w-full max-w-4xl space-y-5 px-4 py-6 sm:px-6">
    <Link className="text-sm text-brand-700 underline" to={`/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}`}>Back to campaign</Link>
    <header><h1 className="break-words text-2xl font-semibold text-slate-900">Campaign output revision</h1><p className="mt-1 break-all text-xs text-slate-500">Output {outputId} · revision {revisionId}</p></header>
    {query.isLoading ? <p role="status" className="text-sm text-slate-600">Loading exact revision…</p>
      : query.error ? <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">This exact revision could not be loaded. {query.error.message}</p>
      : query.data?.state !== "available" || !query.data.content ? <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">This exact revision is unavailable ({query.data?.state ?? "unknown"}).</p>
      : <article className="rounded-xl border border-slate-200 bg-white p-5 sm:p-8">
        <h2 className="text-lg font-medium text-slate-900">{query.data.head.kind ?? query.data.head.family}</h2>
        {query.data.content.format === "markdown" ? <Markdown content={query.data.content.body} reading />
          : query.data.content.format === "text" ? <pre className="mt-4 whitespace-pre-wrap break-words font-sans text-sm leading-7 text-slate-800">{query.data.content.body}</pre>
            : query.data.content.format === "structured" ? <div className="mt-4 space-y-3">{query.data.content.title && <h3 className="font-medium">{query.data.content.title}</h3>}{query.data.content.fields.map(field => <section key={field.key}><h4 className="text-sm font-medium text-slate-700">{field.label}</h4><p className="whitespace-pre-wrap break-words text-sm text-slate-800">{String(field.value ?? "—")}</p></section>)}</div> : null}
      </article>}
  </main>;
}
